import * as React from 'react'
import { Skeleton, message, Tooltip, Icon, Spin } from 'antd'
import { parseAndDownloadCSV, GrPrice, parseErrorMsg } from '../../../../../../../utilities/helpers'
import { API } from '@getgreenline/homi-shared'
import {
  ReportFields,
  IBaseReportData,
  IReportTax,
  IReportV2Query,
} from '@getgreenline/homi-shared/dist/models/Reports/ReportsV2'
import { Track } from '../../../../../../../Track'
import moment from 'moment'
import { Routes } from '../../../../../../../constants/Routes'
import { Link } from 'react-router'
import { isNil, isNumber } from 'lodash'
import { BlazeButton } from '../../../../../../../components/CustomButtons/BlazeButton'
import { BLAZE_COMPANY } from '../../../../../../../constants/general'

type IFinancialOverviewData = Pick<
  IBaseReportData,
  | 'Gross sales'
  | 'Refunds'
  | 'Discounts'
  | 'Net sales'
  | 'Taxes'
  | 'Deposits'
  | 'Gift cards'
  | 'Penny adjustment'
  | 'Total'
  | 'Cost'
  | 'Profit'
  | 'Gift card discounts'
  | 'Gift card refunds'
>

interface Props {
  companyId: number
  startDate: string
  endDate: string
  locationIds: number[]
}

interface State {
  data?: IFinancialOverviewData
  isLoading: boolean
  isOnlineDiscrepancyLoading: boolean
  onlineDiscrepancyTotal?: number
}

export class FinancialOverview extends React.Component<Props, State> {
  fields: (keyof IFinancialOverviewData)[] = [
    'Gross sales',
    'Refunds',
    'Discounts',
    'Net sales',
    'Taxes',
    'Deposits',
    'Gift cards',
    'Penny adjustment',
    'Total',
    'Cost',
    'Profit',
    'Gift card discounts',
    'Gift card refunds',
  ]

  constructor(props: Props) {
    super(props)
    this.state = {
      data: undefined,
      isLoading: false,
      isOnlineDiscrepancyLoading: false,
      onlineDiscrepancyTotal: undefined,
    }
  }

  componentDidMount = async () => {
    await this.getResults()
    await this.getOnlineDiscrepancy()
  }

  getOnlineDiscrepancy = async () => {
    this.setState({ isOnlineDiscrepancyLoading: true })
    try {
      const { companyId } = this.props
      // Api should return 0 if there is no online discrepancy for the payment
      const onlineDiscrepancy = await API.getOnlineDiscrepancy(companyId, this.query)
      const onlineDiscrepancyTotal = isNil(onlineDiscrepancy[0].onlineDiscrepancyTotal)
        ? 0
        : onlineDiscrepancy[0].onlineDiscrepancyTotal
      this.setState({ onlineDiscrepancyTotal })
    } catch (error) {
      this.setState({ onlineDiscrepancyTotal: undefined })
      message.error(
        parseErrorMsg(error) || 'There was an error while loading Online discrepancy report',
      )
    } finally {
      this.setState({ isOnlineDiscrepancyLoading: false })
    }
  }

  get query(): IReportV2Query {
    const { startDate, endDate, locationIds } = this.props

    return {
      locationIds,
      startDate,
      endDate,
      groupBy: {
        time: null,
        dimensions: null,
      },
      orderBy: {
        field: null,
        direction: null,
      },
      fields: this.fields,
    }
  }

  get renderedTaxes() {
    const taxes = this.taxes.map((tax: IReportTax, index) => {
      const taxTitle = tax.Tax
      return (
        <tr key={index}>
          <td>{taxTitle}</td>
          <td key={taxTitle} className='text-right'>
            {GrPrice.convertDollarToDollar(tax.Amount)}
          </td>
        </tr>
      )
    })

    return taxes
  }

  get tableRenderer() {
    const data = this.state.data
    if (!data) return

    return (
      <table className='table table-sm'>
        <tbody>
          <tr>
            <td>Gross sales</td>
            <td className='text-right'>{GrPrice.convertDollarToDollar(data['Gross sales'])}</td>
          </tr>
          <tr>
            <td>Refunds</td>
            <td className='text-right'>{GrPrice.convertDollarToDollar(data['Refunds'])}</td>
          </tr>
          <tr>
            <td>Discounts</td>
            <td className='text-right'>{GrPrice.convertDollarToDollar(data['Discounts'])}</td>
          </tr>
          <tr>
            <td>
              <b>Net sales</b>
            </td>
            <td className='text-right'>
              <b>{GrPrice.convertDollarToDollar(data['Net sales'])}</b>
            </td>
          </tr>
          {this.renderedTaxes}
          <tr>
            <td>Deposits</td>
            <td className='text-right'>{GrPrice.convertDollarToDollar(data['Deposits'])}</td>
          </tr>
          <tr>
            <td>Penny adjustments</td>
            <td className='text-right'>
              {GrPrice.convertDollarToDollar(data['Penny adjustment'])}
            </td>
          </tr>
          <tr>
            <td>
              <Link
                style={{ color: 'inherit', textDecorationLine: 'underline' }}
                target='_blank'
                to={Routes.DASHBOARD_SALES_LIST(
                  this.props.companyId,
                  `?startDate=${this.props.startDate}&endDate=${this.props.endDate}&showOnlineDiscrepancies=true`,
                )}
              >
                <span className='mr-1'>Online order discrepancies</span>
              </Link>
              <Tooltip
                title={`Some payments from outside ${BLAZE_COMPANY} did not provide accurate prices or taxes. Click to view sales with discrepancies`}
              >
                <Icon type='question-circle' />
              </Tooltip>
            </td>
            <td className='text-right'>
              {!this.state.isOnlineDiscrepancyLoading &&
              isNumber(this.state.onlineDiscrepancyTotal) ? (
                GrPrice.convertDollarToDollar(this.state.onlineDiscrepancyTotal)
              ) : (
                <Spin size='small' />
              )}
            </td>
          </tr>
          <tr>
            <td>Gift card sales</td>
            <td className='text-right'>{GrPrice.convertDollarToDollar(data['Gift cards'])}</td>
          </tr>
          <tr>
            <td>Gift card refunds</td>
            <td className='text-right'>
              {GrPrice.convertDollarToDollar(data['Gift card refunds'])}
            </td>
          </tr>
          <tr>
            <td>Gift card discounts</td>
            <td className='text-right'>
              {GrPrice.convertDollarToDollar(data['Gift card discounts'])}
            </td>
          </tr>
          <tr>
            <td>
              <b>Total</b>
            </td>
            <td className='text-right'>
              <b>{GrPrice.convertDollarToDollar(data['Total'])}</b>
            </td>
          </tr>
          <tr>
            <td>Costs</td>
            <td className='text-right'>{GrPrice.convertDollarToDollar(data['Cost'])}</td>
          </tr>
          <tr>
            <td>Profit (net - costs)</td>
            <td className='text-right'>{GrPrice.convertDollarToDollar(data['Profit'])}</td>
          </tr>
        </tbody>
      </table>
    )
  }

  getResults = async () => {
    const { companyId } = this.props
    this.setState({ isLoading: true })
    try {
      Track.startTimer('dashboard:reports_summaryFinancialOverview')
      const results = await API.getSales(companyId, this.query)
      Track.track('dashboard:reports_summaryFinancialOverview', {
        numberOfDays: moment(this.props.endDate).diff(this.props.startDate, 'days'),
      })
      this.setState({ data: results[0] })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error while loading overview report')
      this.setState({ data: undefined })
    } finally {
      this.setState({ isLoading: false })
    }
  }

  get taxes(): IReportTax[] {
    if (!this.state.data) {
      return []
    }
    if (!this.state.data['Taxes']) {
      return []
    }
    const parsedTax: IReportTax[] = this.state.data['Taxes'].filter(
      (tax: IReportTax) => tax.Tax !== null,
    )
    return parsedTax
  }

  orderedExportData = (data: IFinancialOverviewData, onlineDiscrepancyTotal: number) => {
    const taxObjects: Object = this.taxes.reduce((acc, cur) => {
      return { ...acc, [cur.Tax]: cur.Amount }
    }, {})

    let orderedObject = {}

    for (const field of this.fields) {
      if (field === 'Taxes') {
        orderedObject = { ...orderedObject, ...taxObjects }
        continue
      }

      const item = data[field]

      orderedObject[field] = typeof item === 'number' ? parseFloat(`${item}`).toFixed(2) : item

      if (field === 'Penny adjustment') {
        orderedObject['Online order discrepancies'] = parseFloat(
          `${onlineDiscrepancyTotal}`,
        ).toFixed(2)
      }
    }

    return orderedObject
  }

  exportCSV() {
    const { startDate, endDate } = this.props
    if (!this.state.data || !isNumber(this.state.onlineDiscrepancyTotal)) {
      message.warn('Please wait until the overview data to loaded completely')
      return
    }

    const data = this.orderedExportData(this.state.data, this.state.onlineDiscrepancyTotal)

    if (data) {
      parseAndDownloadCSV(`overview-${startDate}-${endDate}`, [data])
    }
  }

  render() {
    const { isLoading } = this.state
    return (
      <div className='w-100'>
        <div className='rounded background-white p-3 mr-3 mb-3'>
          <div>
            <b>Overview</b>
          </div>

          <div className='mt-2 mb-2'>
            <BlazeButton
              buttonProps={{
                type: 'default',
                size: 'default',
                onClick: () => {
                  this.exportCSV()
                },
              }}
              titleProps={{
                title: 'Export as csv',
              }}
            />
          </div>

          <div>{isLoading ? <Skeleton active /> : this.tableRenderer}</div>
        </div>
      </div>
    )
  }
}
